/**
 * HideAmountsContext — a single global switch that turns every tagged
 * dollar/percentage/asset-name into dots (••••••) across the app.
 *
 * How it works:
 *   1. State lives in one Context so any component can flip it.
 *   2. The choice is persisted per-device via localStorage.
 *   3. When ON, we set `data-hide-amounts="true"` on the app root.
 *   4. A tiny stylesheet injected below hides every element carrying a
 *      `data-sensitive` attribute — the same tagging system MaskedOverlay
 *      already uses for co-viewing.
 *
 * This piggybacks on the existing tagging effort so the toggle covers
 * everywhere co-viewing already covered (dashboard, holdings, allocation)
 * on day one. Tagging gaps for other pages will be filled incrementally.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';

const STORAGE_KEY = 'mif:hide_amounts';

const HideAmountsContext = createContext({
  hideAmounts: false,
  toggleHideAmounts: () => {},
});

/* CSS is scoped to `[data-hide-amounts="true"]` on the root so it never
   interferes with the co-viewing mask (which uses `[data-masked-active]`). */
const HIDE_STYLES = `
[data-hide-amounts="true"] [data-sensitive="monetary"],
[data-hide-amounts="true"] [data-sensitive="currency"],
[data-hide-amounts="true"] [data-sensitive="rate"] {
  color: transparent !important;
  text-shadow: none !important;
  background: repeating-linear-gradient(
    90deg,
    #94a3b8 0px, #94a3b8 6px,
    transparent 6px, transparent 8px
  );
  background-size: 100% 55%;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 2px;
  user-select: none;
  min-width: 48px;
  display: inline-block;
}
[data-hide-amounts="true"] [data-sensitive="asset-name"],
[data-hide-amounts="true"] [data-sensitive="identifier"] {
  color: transparent !important;
  background: linear-gradient(90deg, #64748b 0%, #64748b 35%, transparent 35%, transparent 40%, #94a3b8 40%, #94a3b8 100%);
  background-size: 100% 55%;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 2px;
  user-select: none;
  min-width: 64px;
  display: inline-block;
}
[data-hide-amounts="true"] [data-sensitive="chart"],
[data-hide-amounts="true"] canvas[data-sensitive] {
  filter: blur(10px) !important;
  pointer-events: none;
}
`;

const readInitial = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch { return false; }
};

export function HideAmountsProvider({ children }) {
  const [hideAmounts, setHide] = useState(readInitial);

  // Inject stylesheet once.
  useEffect(() => {
    if (document.getElementById('hide-amounts-styles')) return;
    const el = document.createElement('style');
    el.id = 'hide-amounts-styles';
    el.textContent = HIDE_STYLES;
    document.head.appendChild(el);
  }, []);

  // Reflect state on the document root + persist.
  useEffect(() => {
    document.documentElement.setAttribute(
      'data-hide-amounts',
      hideAmounts ? 'true' : 'false'
    );
    try {
      localStorage.setItem(STORAGE_KEY, hideAmounts ? '1' : '0');
    } catch { /* private mode — silently degrade */ }
  }, [hideAmounts]);

  const toggleHideAmounts = useCallback(() => setHide((v) => !v), []);

  const value = useMemo(
    () => ({ hideAmounts, toggleHideAmounts }),
    [hideAmounts, toggleHideAmounts]
  );

  return (
    <HideAmountsContext.Provider value={value}>
      {children}
    </HideAmountsContext.Provider>
  );
}

export function useHideAmounts() {
  return useContext(HideAmountsContext);
}
